import type { ForecastSlot } from "./api";

/**
 * The arithmetic behind the Forecast page's pictures: the pressure windows, the bars
 * stacked by airline, the strip of where the day gets tight and the colour an accuracy
 * score earns. Kept apart from the components so each can be checked on its own, because
 * a stack that is drawn a few passengers short looks exactly like one drawn right.
 */

export const QUARTER = 15 * 60_000;

export type LaneBand = "ok" | "warn" | "bad";

/**
 * How the lanes needed in a quarter hour compare with the lanes there are: under is
 * comfortable, at is tight (nothing can go down), over is a queue. One function so the
 * tiles, the bars, the lane strip and the readout agree on every edge.
 */
export function laneBand(lanesNeeded: number, lanes: number): LaneBand {
  if (lanesNeeded > lanes) return "bad";
  if (lanesNeeded === lanes) return "warn";
  return "ok";
}

export const LANE_TEXT: Record<LaneBand, string> = { ok: "text-ok", warn: "text-warn", bad: "text-bad" };
/** The plain bar colours: blue while it clears, amber at the limit, red past it. */
export const LANE_BAR: Record<LaneBand, string> = { ok: "bg-info", warn: "bg-warn", bad: "bg-bad" };

export interface Pressure {
  kind: "over" | "tight";
  from: number;
  to: number;
  quarters: number;
  peak: number;
  lanesNeeded: number;
  flights: string[];
}

/**
 * Stretches where the checkpoint is at or past what it can clear, run together into one
 * entry each. A list of 40 minutes as three separate rows is the same fact said badly.
 * An "over" quarter hour absorbs a "tight" run rather than starting a new row.
 */
export function pressureWindows(slots: ForecastSlot[], lanes: number): Pressure[] {
  const out: Pressure[] = [];
  let run: Pressure | null = null;
  for (const s of slots) {
    const band = laneBand(s.lanesNeeded, lanes);
    const kind = band === "bad" ? "over" : band === "warn" ? "tight" : null;
    if (!kind) {
      run = null;
      continue;
    }
    if (run && (run.kind === kind || kind === "over")) {
      run.kind = kind === "over" ? "over" : run.kind;
      run.to = s.at + QUARTER;
      run.quarters++;
      run.peak = Math.max(run.peak, s.passengers);
      run.lanesNeeded = Math.max(run.lanesNeeded, s.lanesNeeded);
      for (const f of s.top) if (!run.flights.includes(f.flight)) run.flights.push(f.flight);
      continue;
    }
    run = { kind, from: s.at, to: s.at + QUARTER, quarters: 1, peak: s.passengers, lanesNeeded: s.lanesNeeded, flights: s.top.map((f) => f.flight) };
    out.push(run);
  }
  return out;
}

export interface StripSegment {
  kind: Pressure["kind"];
  /** Left edge and width in the strip's 0..100 space. */
  x: number;
  w: number;
  from: number;
  to: number;
}

/**
 * The pressure windows laid along the day as a strip: each window becomes a coloured
 * segment at its place between the first quarter hour on the chart and the end of the
 * last. A window outside that span is clipped to it rather than drawn off the edge.
 */
export function pressureStrip(windows: Pressure[], first: number, end: number): StripSegment[] {
  const span = Math.max(1, end - first);
  return windows
    .map((w) => {
      const from = Math.max(first, w.from);
      const to = Math.min(end, w.to);
      return { kind: w.kind, x: ((from - first) / span) * 100, w: ((to - from) / span) * 100, from, to };
    })
    .filter((s) => s.w > 0);
}

/**
 * The airlines putting people through security that day, most passengers first, so the
 * legend and every stacked bar use the same order: an airline is always the same layer
 * of the stack, and the biggest sits on the base where it is easiest to read.
 */
export function airlineOrder(slots: ForecastSlot[]): string[] {
  const totals = new Map<string, number>();
  for (const s of slots) for (const f of s.top) totals.set(f.airline, (totals.get(f.airline) ?? 0) + f.passengers);
  return [...totals.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([airline]) => airline);
}

export interface StackSegment {
  /** The airline's code, or null for the people the slot's attribution does not name. */
  airline: string | null;
  passengers: number;
  /** Top edge and height in the bar's 0..100 space, measured from the top of the chart. */
  y: number;
  h: number;
}

/**
 * One quarter hour's bar as layers, one per airline in the order given, with whatever
 * the attribution leaves unnamed on top. The slot only names its biggest flights, each
 * rounded on its own, so the named layers are scaled to fit the slot's total when
 * rounding pushes them past it: the stack always reaches exactly the slot's height.
 */
export function stackSlot(slot: ForecastSlot, order: string[], top: number): StackSegment[] {
  const scale = Math.max(1, top);
  const named = new Map<string, number>();
  for (const f of slot.top) named.set(f.airline, (named.get(f.airline) ?? 0) + f.passengers);
  const sum = [...named.values()].reduce((n, v) => n + v, 0);
  const fit = sum > slot.passengers && sum > 0 ? slot.passengers / sum : 1;

  // Built from the base up: each layer's top edge is worked from the cumulative total so
  // far, and its height from the two edges, so the layers tile without a gap and the top
  // of the stack lands exactly where a plain bar of the slot's total would.
  const segments: StackSegment[] = [];
  const edge = (people: number) => 100 - (people / scale) * 100;
  let filled = 0;
  const airlines = [...order.filter((a) => named.has(a)), ...[...named.keys()].filter((a) => !order.includes(a))];
  for (const airline of airlines) {
    const passengers = (named.get(airline) ?? 0) * fit;
    if (passengers <= 0) continue;
    const y = edge(filled + passengers);
    segments.push({ airline, passengers: Math.round(passengers), y, h: edge(filled) - y });
    filled += passengers;
  }
  const rest = slot.passengers - filled;
  if (rest > 0) {
    const y = edge(slot.passengers);
    segments.push({ airline: null, passengers: Math.round(rest), y, h: edge(filled) - y });
  }
  return segments;
}

export type AccuracyBand = "none" | "close" | "off" | "wide";

export interface AccuracyTone {
  band: AccuracyBand;
  label: string;
  bar: string;
  text: string;
}

const ACCURACY: Record<AccuracyBand, AccuracyTone> = {
  none: { band: "none", label: "not scored", bar: "bg-ink-3/60", text: "text-ink-3" },
  close: { band: "close", label: "within 10%", bar: "bg-ok", text: "text-ok" },
  off: { band: "off", label: "within 25%", bar: "bg-warn", text: "text-warn" },
  wide: { band: "wide", label: "further out", bar: "bg-bad", text: "text-bad" },
};

/**
 * How close a forecast came, in either direction: within 10% is as good as the inputs
 * allow, within 25% is worth a look, and past that the day's inputs were wrong somewhere.
 * The same edges the "So far" line and the accuracy table have always used.
 */
export function accuracyTone(deltaPct: number | null): AccuracyTone {
  if (deltaPct == null || Number.isNaN(deltaPct)) return ACCURACY.none;
  const off = Math.abs(deltaPct);
  if (off <= 10) return ACCURACY.close;
  if (off <= 25) return ACCURACY.off;
  return ACCURACY.wide;
}

/** The bands in the order a legend shows them. */
export const ACCURACY_BANDS: AccuracyBand[] = ["close", "off", "wide"];
export const accuracyBand = (band: AccuracyBand): AccuracyTone => ACCURACY[band];
